import { SubscriptionPlanType } from '../../common/interfaces/entities';
import { PaymentOrderSource } from '../../common/interfaces/payment-order-source';
import { CompanyProductTrialInformationDetail } from '../../common/interfaces/product-free-trial-offer';
import {
  PremiumPlanPurchaseDetailsType,
  PurchaseOrderPurchaseType,
  VIPMembershipPurchaseType,
} from '../CreditSystem/types';
import { TransactionStatus } from './types/transaction-status';

export { PaymentOrderSource, TransactionStatus };

export const PaymentOrderProvider = {
  XENDIT: 'XENDIT',
  TWO_C_TWO_P: '2C2P',
  REVENUE_CAT: 'REVENUE_CAT',
} as const;
export type PaymentOrderProvider =
  (typeof PaymentOrderProvider)[keyof typeof PaymentOrderProvider];

export const PaymentOrderMethod = {
  QRIS: 'QRIS',
  QR_CODE: 'QR_CODE',
  PAYMENT_SESSION: 'PAYMENT_SESSION',
  PAYMENT_TOKEN: 'PAYMENT_TOKEN',
} as const;
export type PaymentOrderMethod =
  (typeof PaymentOrderMethod)[keyof typeof PaymentOrderMethod];

export const PaymentOrderContext = {
  QR: 'QR',
  HOSTED_PAGE: 'HOSTED_PAGE',
} as const;
export type PaymentOrderContext =
  (typeof PaymentOrderContext)[keyof typeof PaymentOrderContext];

export const PaymentOrderType = {
  VIRTUAL_PRODUCT: 'VIRTUAL_PRODUCT',
  RECHARGE: 'RECHARGE',
} as const;
export type PaymentOrderType =
  (typeof PaymentOrderType)[keyof typeof PaymentOrderType];

export const PaymentOrderStatus = {
  PENDING_PAYMENT: 'PENDING_PAYMENT',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CLOSED: 'CLOSED',
  REFUNDED: 'REFUNDED',
  EXPIRED: 'EXPIRED',
} as const;
export type PaymentOrderStatus =
  (typeof PaymentOrderStatus)[keyof typeof PaymentOrderStatus];

export const PENDING_PAYMENT_ORDER_STATUSES: PaymentOrderStatus[] = [
  PaymentOrderStatus.PENDING_PAYMENT,
  PaymentOrderStatus.PARTIALLY_PAID,
];

export const isPendingPaymentOrderStatus = (
  status: PaymentOrderStatus
): boolean => PENDING_PAYMENT_ORDER_STATUSES.includes(status);

export const PaymentOrderPaymentType = {
  CREDITS: 'CREDITS',
  EXTERNAL: 'EXTERNAL',
  COMBINED: 'COMBINED',
} as const;
export type PaymentOrderPaymentType =
  (typeof PaymentOrderPaymentType)[keyof typeof PaymentOrderPaymentType];

export const PaymentPayStatus = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const;
export type PaymentPayStatus =
  (typeof PaymentPayStatus)[keyof typeof PaymentPayStatus];

export const PaymentOrderErrorCode = {
  OK: 'OK',
  BAD_REQUEST: 'BAD_REQUEST',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  USER_NOT_AUTHORIZED: 'USER_NOT_AUTHORIZED',
  CONFLICT: 'CONFLICT',
  FORBIDDEN: 'FORBIDDEN',
  RECORD_NOT_FOUND: 'RECORD_NOT_FOUND',
  NO_PERMISSION: 'NO_PERMISSION',
  USER_ROLE_MISMATCH: 'USER_ROLE_MISMATCH',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  INVALID_ORDER_STATUS: 'INVALID_ORDER_STATUS',
  PAYMENT_PROVIDER_NOT_AVAILABLE_FOR_ORDER:
    'PAYMENT_PROVIDER_NOT_AVAILABLE_FOR_ORDER',
  PAYMENT_PROVIDER_REQUEST_FAILED: 'PAYMENT_PROVIDER_REQUEST_FAILED',
  UNSUPPORTED_PROVIDER: 'UNSUPPORTED_PROVIDER',
  UNSUPPORTED_PAYMENT_METHOD: 'UNSUPPORTED_PAYMENT_METHOD',
  UNAUTHORIZED_PURCHASE_TYPE: 'UNAUTHORIZED_PURCHASE_TYPE',
  ORDER_OWNERSHIP_VIOLATION: 'ORDER_OWNERSHIP_VIOLATION',
} as const;
export type PaymentOrderErrorCode =
  (typeof PaymentOrderErrorCode)[keyof typeof PaymentOrderErrorCode];

export type TaxInfo = {
  amount: number;
  ratePercent: number;
};

export type AvailablePaymentProvider = {
  providerType: PaymentOrderProvider;
  displayName: string;
  paymentMethods: string[] | null;
};

export type PaymentOrderError = {
  title: string;
  details?: string[];
};

export type PaymentOrderApiResponse<T> = {
  code: string;
  data?: T;
  error?: PaymentOrderError;
};

export type VirtualProductOrderBizContextInput = {
  sourcePage: string;
  jobId?: string;
};

export type RechargeOrderBizContextInput = {
  sourcePage: string;
};

export type CreateVirtualProductOrderReq = {
  skuId: string;
  companyId: string;
  currency: string;
  countryCode: string;
  purchaseType: PurchaseOrderPurchaseType;
  price: number;
  additionalJobSlots?: number;
  bizContext: VirtualProductOrderBizContextInput;
};

export type CreateVirtualProductOrderResp = {
  orderNumber: string;
  subscriptionId: string | null;
  orderSource: PaymentOrderSource;
  status: PaymentOrderStatus;
  paymentType: PaymentOrderPaymentType;
  purchaseType: PurchaseOrderPurchaseType;
  deductedCredits: number;
  tax: TaxInfo;
  sellingAmount: number;
  payAmount: number;
  currency: string;
  availablePaymentProviders: AvailablePaymentProvider[] | null;
  message: string | null;
  expiresAt: string | null;
  createdAt: string;
  promotionSnapshot?: {
    creditBenefit?: {
      amount: number;
      frequency?: string;
    } | null;
  } | null;
};

export type CreateRechargeOrderReq = {
  packageId: string;
  companyId: string;
  currency: string;
  countryCode: string;
  bizContext: RechargeOrderBizContextInput;
};

export type PackageDetails = {
  packageId: string;
  paidCredits: number;
  freeCredits: number;
  sellingPrice: number;
  currency: string;
};

export type CreateRechargeOrderResp = {
  orderNumber: string;
  orderSource: PaymentOrderSource;
  status: PaymentOrderStatus;
  packageDetails: PackageDetails;
  tax: TaxInfo;
  payAmount: number;
  sellingAmount: number;
  currency: string;
  availablePaymentProviders: AvailablePaymentProvider[] | null;
  expiresAt: string | null;
  createdAt: string;
};

export type InitiatePaymentReq = {
  orderNumber: string;
  orderType: PaymentOrderType;
  providerType: PaymentOrderProvider;
  paymentMethod: PaymentOrderMethod;
  purchaseType: PurchaseOrderPurchaseType;
};

export type QRISPaymentData = {
  qrString: string;
  qrStringExpiresAt: string;
};

export type PaymentSessionPaymentData = {
  redirectUrl: string;
  redirectUrlExpiresAt: string;
};

export type PaymentTokenPaymentData = {
  redirectUrl: string;
  redirectUrlExpiresAt: string;
  qrString?: string | null;
  qrStringExpiresAt?: string | null;
};

export type InitiatePaymentResp = {
  paymentNumber: string;
  orderNumber: string;
  providerType: PaymentOrderProvider;
  paymentMethod: PaymentOrderMethod;
  payStatus: PaymentPayStatus;
  paymentData:
    | QRISPaymentData
    | PaymentSessionPaymentData
    | PaymentTokenPaymentData;
};

export type BizContext = {
  sourcePage: string | null;
  jobId?: string | null;
};

export type PaymentDetails = {
  paymentStatus: PaymentPayStatus;
  providerCode?: string;
  paymentUrl?: string;
  isOrderCancellationPossible?: boolean;
  transactionStatus?: TransactionStatus;
};

export type RechargeOrderStatusResp = {
  orderNumber: string;
  orderStatus: PaymentOrderStatus;
  displayStatus: OrderListStatus;
  paidAmount: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
  expiryTime: string | null;
  countryCode: string;
  companyId: string;
  userId: string;
  referenceId: string | null;
  creditPackageId: string;
  purchaseCredits: number;
  giftCredits: number;
  totalCredits: number;
  tax: TaxInfo | null;
  bizContext: BizContext | null;
  paymentDetails: PaymentDetails | null;
};

export type VirtualProductOrderStatusResp = {
  orderNumber: string;
  orderStatus: PaymentOrderStatus;
  displayStatus: OrderListStatus;
  paidAmount: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
  expiryTime: string | null;
  countryCode: string;
  companyId: string;
  userId: string;
  referenceId: string | null;
  skuId: string;
  productId: string;
  paymentType: PaymentOrderPaymentType;
  purchaseType: PurchaseOrderPurchaseType;
  paidCredits: number;
  tax: TaxInfo | null;
  bizContext: BizContext | null;
  productDetails: {
    productType: string;
    productName: string;
    planType?: SubscriptionPlanType;
    boostPeriod?: number;
    boostPeriodUnit?: string;
  };
  metadata?: {
    acquisitionType: VIPMembershipPurchaseType | PremiumPlanPurchaseDetailsType;
  };
  paymentDetails: PaymentDetails | null;
  failureType?: string | null;
  companyProductInventory?: {
    amount: number;
    expiryDateTime: string;
    trialInformationDetail?: Pick<
      CompanyProductTrialInformationDetail,
      'isActive' | 'isExtended' | 'endDate'
    > | null;
  } | null;
  subscriptionInfo?: {
    nextRenewalTime: string | null;
  } | null;
  promotionSnapshot?: {
    creditBenefit?: {
      amount: number;
    } | null;
  } | null;
};

export type OrderStatusResponse = {
  virtualProductOrder: VirtualProductOrderStatusResp | null;
  rechargeOrder: RechargeOrderStatusResp | null;
};

export type GetPurchasePreviewReq = {
  orderType: PaymentOrderType;
  companyId: string;
  skuId?: string;
  packageId?: string;
  additionalJobSlot?: number;
};

export type GetPurchasePreviewResp = {
  sellingAmount: number;
  paidAmount: number;
  currencyCode: string;
  tax: TaxInfo | null;
  totalAvailableCredits: number;
  productCreditPrice: number;
};

export type ProductPrice = {
  countryCode: string;
  currencyCode: string;
  regularPrice: number;
  sellingPrice: number;
  regularCredits: number;
  sellingCredits: number;
  discountPercentage: number | null;
};

export type ProductCustomisationsCreditPricing = {
  additionalJobSlot: Record<string, number>;
  additionalDiscount: Record<string, number>;
};

export type CreditBenefit = {
  amount: number;
  frequency?: string;
};
export type ProductBenefit = {
  type: string;
  amount: number;
  additionalJobSlot?: number;
};
export type PromotionInfo = {
  creditBenefit?: CreditBenefit | null;
  productBenefits?: ProductBenefit[] | null;
};

export const OrderListStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED',
  FAILED: 'FAILED',
  REFUNDED: 'REFUNDED',
} as const;
export type OrderListStatus =
  (typeof OrderListStatus)[keyof typeof OrderListStatus];

export type GetOrdersReq = {
  companyId: string;
  cursor?: string;
  pageSize?: number;
  orderStatus?: OrderListStatus;
};

export type OrderItemDetails = {
  type?: string;
  defaultName?: string;
  translatedName: string;
  translationKey: string;
  timePeriod?: number;
  timePeriodUnit?: string;
};

export type OrderListingPaymentDetails = {
  paymentStatus?: string | null;
  providerCode?: string;
  providerName?: string;
  paymentUrl?: string | null;
  invoiceUrl?: string | null;
  isOrderCancellationPossible?: boolean;
};

export type VirtualProductOrderSummaryFields = {
  skuId?: string;
  purchaseType?: PurchaseOrderPurchaseType;
  paidCredits?: number;
  sellingCredits?: number;
};

export type RechargeOrderSummaryFields = {
  creditPackageId?: string;
  purchaseCredits?: number;
  giftCredits?: number;
  totalCredits?: number;
};

export type OrderBuyer = {
  id: string;
  name: string;
};

export type BaseOrderSummary = {
  orderNumber: string;
  orderType: PaymentOrderType;
  status: PaymentOrderStatus;
  displayStatus: OrderListStatus;
  paidAmount: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
  expiryTime?: string | null;
  paymentDetails?: OrderListingPaymentDetails | null;
  itemDetails?: OrderItemDetails | null;
  buyer?: OrderBuyer | null;
};

export type OrderSummary = BaseOrderSummary &
  (
    | ({
        orderType: typeof PaymentOrderType.VIRTUAL_PRODUCT;
      } & VirtualProductOrderSummaryFields)
    | ({
        orderType: typeof PaymentOrderType.RECHARGE;
      } & RechargeOrderSummaryFields)
  );

export const NudgeBadge = {
  PENDING: 'PENDING',
} as const;
export type NudgeBadge = (typeof NudgeBadge)[keyof typeof NudgeBadge];

export type GetOrdersResp = {
  orders: OrderSummary[];
  hasMore: boolean;
  nextCursor: string;
  nudgeBadges?: NudgeBadge[];
};

export type ProductItem = {
  skuId: string;
  sku: string;
  type: string;
  benefitType: string;
  purchaseType: 'ONE_TIME' | 'SUBSCRIPTION';
  productInfo: {
    productId: string;
    quantity: number;
    timePeriod: number;
    defaultName: string;
    expiryInDays?: number;
    timePeriodUnit: string;
    translationKey: string;
    timePeriodValidityType: string;
    boostPeriod?: number;
    boostPeriodUnit?: string;
    planType?: SubscriptionPlanType;
    tag?: string | null;
    customisationsCreditPricing?: ProductCustomisationsCreditPricing;
  } | null;
  price: ProductPrice | null;
  promotionInfo?: PromotionInfo | null;
};
